import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useLoginCheck } from "../../../../hooks/useLoginCheck";
import "../../../../styles/scss/recital2027Timetable.scss";
import {
  KIND_LABELS,
  MAIN_STAGES,
  PERSON_TYPE_LABELS,
  STAGES,
  STAGE_LABELS,
  computeTimetable,
  durationOf,
  fmtClock,
  fmtDur,
  fmtLong,
  isLessonWork,
  minGapAt,
  parseDur,
  slotDurationOf,
  subOf,
} from "./timetableCalc";

/**
 * 発表会2027 タイムテーブル（当日の進行表）。
 *
 * 演目は演目マスター（recital2027_works）、並びは recital2027_program_slots。
 * 並べ替え・枠の挿入などの操作は画面の中で即反映し、少し待ってから3つの部の並びを
 * まとめて保存する（他の人が先に保存していたら 409 で知らせ、上書きしない）。
 * 時刻・番号・連続出演は timetableCalc.js で計算する（サーバーの PDF・CSV と同じ規則）。
 * 設計: docs/recital2027/timetable_design.md
 */

const apiBase = process.env.REACT_APP_API_URL;
const GAP_COLORS = ["", "var(--g1)", "var(--g2)", "var(--g3)", "var(--g4)", "var(--g5)"];
const GAP_HEX = ["", "#ef5a6f", "#f27a4d", "#f5a044", "#e9c84a", "#b9b27c"];
const SEED_LABELS = ["オープニング映像", "エンディング映像", "MC", "休憩"];
const DATES = { part1: "main_date", part2: "main_date", genepro: "genepro_date" };
const SAVE_DELAY_MS = 600;
const SIDE_KEY = "recital2027-timetable-side-open";

let tmpSeq = 0;
const tmpId = () => `tmp-${(tmpSeq += 1)}`;
const isTmp = (id) => typeof id === "string";

const cloneSlots = (slots) => {
  const out = {};
  STAGES.forEach((s) => {
    out[s] = (slots[s] || []).map((x) => ({ ...x }));
  });
  return out;
};

async function api(path, { method = "GET", body } = {}) {
  const res = await fetch(`${apiBase}/api/${path}/`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.error || `失敗しました (${res.status})`);
    err.conflict = !!json.conflict;
    throw err;
  }
  return json;
}

export default function TimetablePage() {
  useLoginCheck();

  const [settings, setSettings] = useState(null);
  const [works, setWorks] = useState({});
  const [slots, setSlots] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saveState, setSaveState] = useState("");
  const [conflict, setConflict] = useState("");
  const [toast, setToast] = useState("");
  const [vis, setVis] = useState({ part1: true, part2: true, genepro: true });
  const [sel, setSel] = useState(null);
  const [insertAt, setInsertAt] = useState(null);
  const [findQ, setFindQ] = useState("");
  const [findKind, setFindKind] = useState("");
  const [flash, setFlash] = useState({});
  const [modal, setModal] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // 左の演目エリアの表示・非表示。開いた人のブラウザにだけ覚えておく（既定は表示）
  const [sideOpen, setSideOpen] = useState(() => {
    try {
      return localStorage.getItem(SIDE_KEY) !== "0";
    } catch (e) {
      return true;
    }
  });
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [dragHint, setDragHint] = useState(null);

  const slotsRef = useRef(null);
  const versionRef = useRef("");
  const savingRef = useRef(false);
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const dragStartRef = useRef(null);
  const toastTimer = useRef(null);
  const menuRef = useRef(null);

  slotsRef.current = slots;

  const showToast = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2800);
  }, []);

  // ------------------------------------------------------------------ 読み込み
  const load = useCallback(async () => {
    setLoadError("");
    try {
      const json = await api("get_recital2027_timetable");
      const map = {};
      json.works.forEach((w) => {
        map[w.id] = w;
      });
      setWorks(map);
      setSettings(json.settings);
      setSlots(json.slots);
      versionRef.current = json.version;
      setPast([]);
      setFuture([]);
      setConflict("");
      setSaveState("");
    } catch (e) {
      setLoadError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ------------------------------------------------------------------ 保存
  const replaceIds = (target, mapping) => {
    if (!target) return target;
    const out = {};
    STAGES.forEach((s) => {
      out[s] = (target[s] || []).map((x) => ({
        ...x,
        id: mapping[x.id] ?? x.id,
        main_slot_id: mapping[x.main_slot_id] ?? x.main_slot_id,
      }));
    });
    return out;
  };

  const save = useCallback(async () => {
    if (savingRef.current) {
      dirtyRef.current = true;
      return;
    }
    const sent = slotsRef.current;
    if (!sent) return;
    savingRef.current = true;
    dirtyRef.current = false;
    setSaveState("保存中…");
    const stages = {};
    STAGES.forEach((s) => {
      stages[s] = sent[s].map((x) => ({
        id: isTmp(x.id) ? null : x.id,
        slot_type: x.slot_type,
        work_id: x.work_id,
        label: x.label,
        duration_sec: x.duration_sec,
        changeover_sec_override: x.changeover_sec_override,
        main_slot_id: isTmp(x.main_slot_id) ? null : x.main_slot_id,
      }));
    });
    try {
      const json = await api("save_recital2027_program_slots", { method: "POST", body: { version: versionRef.current, stages } });
      versionRef.current = json.version;
      // 送った並びと返ってきた並びは同じ順なので、仮のIDを位置で本当のIDに置き換える
      const mapping = {};
      STAGES.forEach((s) => {
        sent[s].forEach((x, i) => {
          const real = json.slots[s][i];
          if (real && isTmp(x.id)) mapping[x.id] = real.id;
        });
      });
      setSlots((cur) => replaceIds(cur, mapping));
      setPast((p) => p.map((snap) => replaceIds(snap, mapping)));
      setFuture((f) => f.map((snap) => replaceIds(snap, mapping)));
      setSel((cur) => mapping[cur] ?? cur);
      setSaveState("保存しました");
    } catch (e) {
      if (e.conflict) setConflict(e.message);
      else showToast(`保存できませんでした：${e.message}`);
      setSaveState("未保存");
    } finally {
      savingRef.current = false;
      if (dirtyRef.current) {
        dirtyRef.current = false;
        setTimeout(() => save(), 0);
      }
    }
  }, [showToast]);

  const scheduleSave = useCallback(() => {
    setSaveState("未保存");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(), SAVE_DELAY_MS);
  }, [save]);

  /** 並びを変える操作は必ずここを通す（戻す・やり直すの履歴と保存）。 */
  const commit = useCallback(
    (next, before) => {
      const prev = before || slotsRef.current;
      setPast((p) => [...p.slice(-60), prev]);
      setFuture([]);
      setSlots(next);
      slotsRef.current = next;
      scheduleSave();
    },
    [scheduleSave]
  );

  const undo = useCallback(() => {
    if (!past.length) return;
    const prev = past[past.length - 1];
    setPast(past.slice(0, -1));
    setFuture((f) => [slotsRef.current, ...f]);
    setSlots(prev);
    slotsRef.current = prev;
    scheduleSave();
    showToast("元に戻しました");
  }, [past, scheduleSave, showToast]);

  const redo = useCallback(() => {
    if (!future.length) return;
    const next = future[0];
    setFuture(future.slice(1));
    setPast((p) => [...p, slotsRef.current]);
    setSlots(next);
    slotsRef.current = next;
    scheduleSave();
    showToast("やり直しました");
  }, [future, scheduleSave, showToast]);

  useEffect(() => {
    const onKey = (e) => {
      const inField = e.target.matches && e.target.matches("input,textarea,select");
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !inField) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
      if (e.key === "Escape") {
        if (modal) setModal(null);
        else if (insertAt) setInsertAt(null);
        else if (sel) setSel(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, modal, insertAt, sel]);

  const toggleSide = () => {
    const next = !sideOpen;
    setSideOpen(next);
    try {
      localStorage.setItem(SIDE_KEY, next ? "1" : "0");
    } catch (e) {
      // 保存できなくても表示の切り替えはできる
    }
  };

  // 出力メニューは、メニューの外を押すか Esc で閉じる
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // 画面を離れる前に、保存待ちがあれば送る
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        save();
      }
    },
    [save]
  );

  // ------------------------------------------------------------------ 設定
  const saveSettings = useCallback(
    async (patch) => {
      setSettings((s) => ({ ...s, ...patch }));
      try {
        const json = await api("save_recital2027_timetable_settings", { method: "POST", body: patch });
        setSettings(json.settings);
        versionRef.current = json.version;
      } catch (e) {
        showToast(`設定を保存できませんでした：${e.message}`);
        load();
      }
    },
    [showToast, load]
  );

  const saveWorkDuration = useCallback(
    async (workId, sec) => {
      const w = works[workId];
      if (!w || w.music_sec != null) return;
      setWorks((m) => ({ ...m, [workId]: { ...m[workId], planned_duration_sec: sec } }));
      try {
        await api("update_recital2027_work_duration", { method: "POST", body: { work_id: workId, duration_sec: sec } });
      } catch (e) {
        showToast(e.message);
        setWorks((m) => ({ ...m, [workId]: w }));
      }
    },
    [works, showToast]
  );

  // ------------------------------------------------------------------ 計算
  const calc = useMemo(
    () => (slots && settings ? computeTimetable(slots, works, settings) : null),
    [slots, works, settings]
  );

  const appsOf = useCallback(
    (workId, stages = MAIN_STAGES) =>
      stages.flatMap((k) => (slots?.[k] || []).filter((s) => s.work_id === workId).map((s) => ({ k, sid: s.id }))),
    [slots]
  );

  const stageOf = useCallback(
    (sid) => STAGES.find((k) => (slots?.[k] || []).some((s) => s.id === sid)) || null,
    [slots]
  );

  const usedLabels = useMemo(() => {
    const set = new Set(SEED_LABELS);
    STAGES.forEach((k) => (slots?.[k] || []).forEach((s) => s.slot_type === "free" && s.label && set.add(s.label)));
    return [...set];
  }, [slots]);

  const unplaced = useMemo(
    () => Object.values(works).filter((w) => !w.delete_flg && !appsOf(w.id).length),
    [works, appsOf]
  );

  // ------------------------------------------------------------------ 操作
  const flashSlots = useCallback((sids, color = "#8aa4ff") => {
    const map = {};
    sids.forEach((sid) => {
      map[sid] = color;
    });
    setFlash(map);
    const first = document.getElementById(`slot-${sids[0]}`);
    if (first) first.scrollIntoView({ block: "center", behavior: "smooth" });
    setTimeout(() => setFlash({}), 2400);
  }, []);

  const selectSlot = (sid, scroll) => {
    setSel(sid);
    if (scroll) setTimeout(() => flashSlots([sid]), 50);
  };

  const addAppearance = (workId, stage) => {
    const next = cloneSlots(slots);
    const id = tmpId();
    next[stage].push({ id, slot_type: "work", work_id: workId, label: "", duration_sec: null, changeover_sec_override: null, main_slot_id: null });
    commit(next);
    selectSlot(id, true);
    showToast(`${STAGE_LABELS[stage]}の末尾に入れました。ドラッグで位置を決めてください`);
  };

  const removeSlot = (sid) => {
    const next = cloneSlots(slots);
    STAGES.forEach((k) => {
      next[k] = next[k].filter((s) => s.id !== sid);
    });
    commit(next);
    if (sel === sid) setSel(null);
  };

  const removeWorkEverywhere = (workId) => {
    const next = cloneSlots(slots);
    STAGES.forEach((k) => {
      next[k] = next[k].filter((s) => s.work_id !== workId);
    });
    commit(next);
    setSel(null);
    setModal(null);
    showToast("タイムテーブルから外しました（⌘Z で戻せます）");
  };

  const requestRemove = (sid) => {
    const k = stageOf(sid);
    const slot = slots[k].find((s) => s.id === sid);
    if (k !== "genepro" && appsOf(slot.work_id).length === 1) {
      setModal({ type: "remove-last", workId: slot.work_id });
      return;
    }
    removeSlot(sid);
    showToast("この出番を外しました（⌘Z で戻せます）");
  };

  const updateSlot = (sid, patch) => {
    const next = cloneSlots(slots);
    STAGES.forEach((k) => {
      next[k] = next[k].map((s) => (s.id === sid ? { ...s, ...patch } : s));
    });
    commit(next);
  };

  const insertFree = (stage, afterSid, label, dur) => {
    const next = cloneSlots(slots);
    const id = tmpId();
    const arr = next[stage];
    const idx = afterSid == null ? 0 : arr.findIndex((s) => s.id === afterSid) + 1;
    arr.splice(idx, 0, { id, slot_type: "free", work_id: null, label, duration_sec: dur, changeover_sec_override: null, main_slot_id: null });
    commit(next);
    setInsertAt(null);
    showToast(`「${label}」を挿入しました`);
  };

  const copyToGenepro = async (withFree) => {
    clearTimeout(timerRef.current);
    if (timerRef.current) await save();
    try {
      const json = await api("copy_recital2027_genepro", { method: "POST", body: { version: versionRef.current, with_free: withFree } });
      versionRef.current = json.version;
      setPast((p) => [...p, slotsRef.current]);
      setFuture([]);
      setSlots(json.slots);
      setModal(null);
      showToast("ゲネプロに本番の並びを複製しました");
    } catch (e) {
      if (e.conflict) setConflict(e.message);
      else showToast(e.message);
    }
  };

  const download = async (path, params, fallbackName) => {
    setMenuOpen(false);
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${apiBase}/api/${path}/?${qs}`, { credentials: "include" });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || `失敗しました (${res.status})`);
      }
      const blob = await res.blob();
      // API は別ドメインで Content-Disposition が読めないことがあるので、名前は画面側でも作る
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const d = new Date();
      const p2 = (n) => String(n).padStart(2, "0");
      const stamp = `${d.getFullYear()}${p2(d.getMonth() + 1)}${p2(d.getDate())}_${p2(d.getHours())}${p2(d.getMinutes())}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : fallbackName.replace(/\.(pdf|csv)$/, `_${stamp}.$1`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) {
      showToast(`出力できませんでした：${e.message}`);
    }
  };

  // ------------------------------------------------------------------ ドラッグ
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const containerOf = (id) => {
    if (STAGES.includes(id)) return id;
    const cur = slotsRef.current;
    return STAGES.find((k) => cur[k].some((s) => String(s.id) === String(id))) || null;
  };

  const onDragStart = ({ active }) => {
    setActiveId(String(active.id));
    dragStartRef.current = slotsRef.current;
    setInsertAt(null);
  };

  const updateHint = (cur, container, activeKey, overId) => {
    const list = cur[container];
    const from = list.findIndex((s) => String(s.id) === activeKey);
    if (from < 0) return setDragHint(null);
    const item = list[from];
    if (item.slot_type !== "work") return setDragHint(null);
    let to = STAGES.includes(String(overId)) ? list.length - 1 : list.findIndex((s) => String(s.id) === String(overId));
    if (to < 0) to = from;
    const items = arrayMove(list, from, to);
    const gap = minGapAt(items, to, item.work_id, works, settings);
    setDragHint({ gap });
  };

  const onDragOver = ({ active, over }) => {
    if (!over) return;
    const activeKey = String(active.id);
    if (activeKey.startsWith("part:")) return;
    const from = containerOf(activeKey);
    const to = containerOf(String(over.id));
    if (!from || !to) return;
    if (from !== to) {
      // 本番（1部・2部）とゲネプロの間は移動させない（ゲネは複製と個別の並べ替えで作る）
      if ((from === "genepro") !== (to === "genepro")) return;
      const cur = cloneSlots(slotsRef.current);
      const idx = cur[from].findIndex((s) => String(s.id) === activeKey);
      const [item] = cur[from].splice(idx, 1);
      let at = cur[to].findIndex((s) => String(s.id) === String(over.id));
      if (at < 0) at = cur[to].length;
      cur[to].splice(at, 0, item);
      slotsRef.current = cur;
      setSlots(cur);
      updateHint(cur, to, activeKey, over.id);
    } else {
      updateHint(slotsRef.current, to, activeKey, over.id);
    }
  };

  const onDragEnd = ({ active, over }) => {
    const activeKey = String(active.id);
    const before = dragStartRef.current;
    setActiveId(null);
    setDragHint(null);
    dragStartRef.current = null;
    if (!over) {
      if (before && before !== slotsRef.current) setSlots(before);
      return;
    }
    const to = containerOf(String(over.id));
    if (activeKey.startsWith("part:")) {
      if (!to) return;
      const label = active.data.current?.label || "新しい枠";
      const next = cloneSlots(slotsRef.current);
      let at = next[to].findIndex((s) => String(s.id) === String(over.id));
      if (at < 0) at = next[to].length;
      next[to].splice(at, 0, {
        id: tmpId(),
        slot_type: "free",
        work_id: null,
        label,
        duration_sec: settings.default_free_slot_sec || 60,
        changeover_sec_override: null,
        main_slot_id: null,
      });
      commit(next, before);
      return;
    }
    if (!to) return;
    const cur = cloneSlots(slotsRef.current);
    const list = cur[to];
    const from = list.findIndex((s) => String(s.id) === activeKey);
    const at = list.findIndex((s) => String(s.id) === String(over.id));
    if (from >= 0 && at >= 0 && from !== at) cur[to] = arrayMove(list, from, at);
    const changed = JSON.stringify(cur) !== JSON.stringify(before);
    if (changed) commit(cur, before);
    else setSlots(before);
  };

  const onDragCancel = () => {
    if (dragStartRef.current) setSlots(dragStartRef.current);
    dragStartRef.current = null;
    setActiveId(null);
    setDragHint(null);
  };

  // ------------------------------------------------------------------ 表示
  if (loadError) {
    return (
      <div className="recital2027-timetable-page">
        <div className="tt-banner tt-error">
          読み込めませんでした：{loadError}
          <button className="tt-btn tt-sm" onClick={load}>
            もう一度読み込む
          </button>
        </div>
      </div>
    );
  }
  if (!slots || !settings || !calc) {
    return <div className="recital2027-timetable-page">読み込み中…</div>;
  }

  const visiblePairs = STAGES.filter((k) => vis[k])
    .flatMap((k) => calc[k].pairs)
    .sort((x, y) => x.gap - y.gap);
  const allWorks = Object.values(works).filter((w) => !w.delete_flg);
  const appearanceCount = MAIN_STAGES.reduce((n, k) => n + slots[k].filter((s) => s.slot_type === "work").length, 0);
  const tentative = allWorks.filter((w) => w.music_sec == null).length;

  const titleOf = (w) => (w ? w.display_title || w.title : "（演目が見つかりません）");
  const labelOfSlot = (k, sid) => {
    const r = calc[k].bySid[sid];
    if (!r) return "";
    return k === "genepro" ? (r.ref ? `G:${r.ref}` : `G${r.pos}`) : r.no || "";
  };

  const activeSlot = activeId && !activeId.startsWith("part:") ? STAGES.flatMap((k) => slots[k]).find((s) => String(s.id) === activeId) : null;

  return (
    <div className="recital2027-timetable-page">
      <div className="tt-header">
        <div>
          <p className="tt-eyebrow">発表会2027 · SLASH GALLERY vol.5</p>
          <h1>タイムテーブル</h1>
        </div>
        <div className="tt-tools">
          <label className="tt-field-inline">
            既定の転換
            <DurInput className="tt-inp tt-s tt-mono" value={settings.default_changeover_sec || 0} onCommit={(v) => saveSettings({ default_changeover_sec: v })} />
          </label>
          <label className="tt-field-inline" title="音源が未提出の単独レッスンの尺">
            既定の尺 単独レッスン
            <DurInput className="tt-inp tt-s tt-mono" value={settings.default_lesson_sec} onCommit={(v) => v > 0 && saveSettings({ default_lesson_sec: v })} />
          </label>
          <label className="tt-field-inline" title="音源が未提出の、単独レッスン以外（合同・選抜・HEADZ・イントラショー・特別ナンバー）の尺">
            それ以外
            <DurInput className="tt-inp tt-s tt-mono" value={settings.default_group_sec} onCommit={(v) => v > 0 && saveSettings({ default_group_sec: v })} />
          </label>
          <span className="tt-sep" />
          {STAGES.map((k) => (
            <label key={k} className="tt-toggle">
              <input type="checkbox" checked={vis[k]} onChange={(e) => setVis({ ...vis, [k]: e.target.checked })} />
              {STAGE_LABELS[k]}
            </label>
          ))}
          <span className="tt-sep" />
          <button className="tt-btn tt-ghost" onClick={undo} disabled={!past.length} title="元に戻す (⌘Z)">
            ↶ 戻す
          </button>
          <button className="tt-btn tt-ghost" onClick={redo} disabled={!future.length} title="やり直す (⇧⌘Z)">
            ↷ やり直す
          </button>
          <span className="tt-save-state">{saveState}</span>
          <div className="tt-menu-wrap" ref={menuRef}>
            <button className="tt-btn tt-primary" onClick={() => setMenuOpen(!menuOpen)}>
              出力 ▾
            </button>
            {menuOpen && (
              <div className="tt-menu">
                <div className="tt-mh">進行表</div>
                <button onClick={() => download("export_recital2027_timetable", { format: "pdf", stage: "all" }, "recital2027_timetable.pdf")}>
                  PDF<span className="tt-dim">全部（A4 縦）</span>
                </button>
                <button onClick={() => download("export_recital2027_timetable", { format: "csv", stage: "all" }, "recital2027_timetable.csv")}>
                  CSV<span className="tt-dim">1行1コマ</span>
                </button>
                <div className="tt-mh">点呼表</div>
                {["all", "part1", "part2"].map((k) => (
                  <button key={k} onClick={() => download("export_recital2027_rollcall", { format: "pdf", stage: k }, "recital2027_rollcall.pdf")}>
                    PDF<span className="tt-dim">{k === "all" ? "全部" : `${STAGE_LABELS[k]}だけ`}</span>
                  </button>
                ))}
                <button onClick={() => download("export_recital2027_rollcall", { format: "csv", stage: "all" }, "recital2027_rollcall.csv")}>
                  CSV<span className="tt-dim">1行1人</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {conflict && (
        <div className="tt-banner tt-error">
          {conflict}
          <button className="tt-btn tt-sm" onClick={load}>
            読み込み直す
          </button>
        </div>
      )}

      <div className="tt-warnbar">
        <div className="tt-lbl">
          連続出演
          <span className="tt-legend" aria-label="何ブロック後に出番があるかの色分け">
            {[1, 2, 3, 4, 5].map((g) => (
              <span key={g} style={{ background: GAP_COLORS[g] }}>
                {g}
              </span>
            ))}
          </span>
          <span className="tt-mono tt-dim">{visiblePairs.length}件</span>
        </div>
        <div className="tt-pairs">
          {visiblePairs.length ? (
            visiblePairs.map((p) => (
              <button key={`${p.stage}-${p.key}-${p.a}`} className="tt-pair" onClick={() => flashSlots([p.a, p.b], GAP_HEX[p.gap])}>
                <span className="tt-gdot" style={{ background: GAP_COLORS[p.gap] }}>
                  {p.gap}
                </span>
                {p.name}
                <span className="tt-dim tt-mono">
                  {labelOfSlot(p.stage, p.a)} → {labelOfSlot(p.stage, p.b)}
                </span>
              </button>
            ))
          ) : (
            <span className="tt-ok-note">表示中の部に、{settings.warn_gap_max}ブロック以内の連続出演はありません</span>
          )}
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <div className={`tt-app ${sideOpen ? "" : "is-side-closed"}`}>
          {sideOpen ? (
            <div className="tt-side">
              <div className="tt-side-sec">
                <div className="tt-side-top">
                  <span>演目</span>
                  <button className="tt-btn tt-sm tt-ghost" onClick={toggleSide} title="演目のエリアをたたむ" aria-label="演目のエリアをたたむ">
                    ‹
                  </button>
                </div>
                <div className="tt-stats">
                  <div>
                    演目<b>{allWorks.length}</b>
                  </div>
                  <div>
                    本番の出番<b>{appearanceCount}</b>
                  </div>
                  <div title="音源がまだ提出されておらず、尺が仮の演目">
                    尺が仮<b>{tentative}</b>
                  </div>
                </div>
              </div>
              <div className="tt-side-sec">
                <h2>タイムテーブルに入っていない演目</h2>
                <div className="tt-unplaced">
                  {unplaced.length ? (
                    unplaced.map((w) => (
                      <div key={w.id} className="tt-unp">
                        <div className="tt-t1">{titleOf(w)}</div>
                        <div className="tt-t2 tt-dim">{subOf(w)}</div>
                        <div className="tt-row3">
                          <button className="tt-btn tt-sm" onClick={() => addAppearance(w.id, "part1")}>
                            1部の末尾へ
                          </button>
                          <button className="tt-btn tt-sm" onClick={() => addAppearance(w.id, "part2")}>
                            2部の末尾へ
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="tt-ok-box">すべての演目がタイムテーブルに入っています</div>
                  )}
                </div>
              </div>
              <div className="tt-side-sec">
                <h2>演目を探す</h2>
                <input className="tt-inp" style={{ width: "100%" }} placeholder="店舗・イントラ・演目名・生徒名" value={findQ} onChange={(e) => setFindQ(e.target.value)} />
                <select className="tt-inp" style={{ width: "100%", marginTop: 6 }} value={findKind} onChange={(e) => setFindKind(e.target.value)}>
                  <option value="">すべての種別</option>
                  {Object.entries(KIND_LABELS).map(([k, l]) => (
                    <option key={k} value={k}>
                      {l}
                    </option>
                  ))}
                  <option value="multi">2回以上出る演目</option>
                </select>
              </div>
              <FindList
                works={allWorks}
                q={findQ}
                kind={findKind}
                appsOf={appsOf}
                calc={calc}
                titleOf={titleOf}
                onPick={(sid) => selectSlot(sid, true)}
              />
              <div className="tt-side-sec" style={{ borderTop: "1px solid var(--border)", borderBottom: 0 }}>
                <h2>作品以外の枠</h2>
                <div className="tt-parts">
                  {usedLabels.map((l, i) => (
                    <Part key={l} id={`part:${i}`} label={l} />
                  ))}
                  <Part id="part:new" label="新しい枠" className="tt-new" text="＋ 新しい枠" />
                </div>
                <div className="tt-hint-s">名前も尺も自由入力。列の好きな位置へドラッグするか、行の間の「＋ 枠」から入れます。</div>
              </div>
            </div>
          ) : (
            <button className="tt-side-rail" onClick={toggleSide} title="演目のエリアを表示" aria-label="演目のエリアを表示">
              ›
            </button>
          )}

          <div className="tt-board">
            {STAGES.filter((k) => vis[k]).map((k) => (
              <StageColumn
                key={k}
                stage={k}
                slots={slots[k]}
                calc={calc}
                settings={settings}
                works={works}
                sel={sel}
                flash={flash}
                insertAt={insertAt}
                usedLabels={usedLabels}
                appsOf={appsOf}
                titleOf={titleOf}
                onSelect={(sid) => setSel(sel === sid ? null : sid)}
                onSettings={saveSettings}
                onInsertAt={setInsertAt}
                onInsert={insertFree}
                onUpdateSlot={updateSlot}
                onRemoveSlot={(sid) => {
                  removeSlot(sid);
                  showToast("枠を削除しました（⌘Z で戻せます）");
                }}
                onWorkDuration={saveWorkDuration}
                onCopyGenepro={() => setModal({ type: "copy" })}
              />
            ))}
          </div>

          {sel && stageOf(sel) && (
            <Detail
              sid={sel}
              stage={stageOf(sel)}
              slots={slots}
              works={works}
              calc={calc}
              settings={settings}
              appsOf={appsOf}
              titleOf={titleOf}
              onClose={() => setSel(null)}
              onGoto={(sid) => selectSlot(sid, true)}
              onAddAppearance={addAppearance}
              onRemove={requestRemove}
              onWorkDuration={saveWorkDuration}
              onUpdateSlot={updateSlot}
              onRollcall={(sid) => download("export_recital2027_rollcall", { format: "pdf", slot_id: sid }, "recital2027_rollcall.pdf")}
            />
          )}
        </div>
        <DragOverlay>
          {activeId &&
            (activeId.startsWith("part:") ? (
              <div className="tt-overlay-row">枠を挿入</div>
            ) : activeSlot ? (
              <div className="tt-overlay-row">
                {activeSlot.slot_type === "work" ? titleOf(works[activeSlot.work_id]) : activeSlot.label}
                {dragHint && activeSlot.slot_type === "work" && (
                  <span className="tt-gapb" style={{ background: dragHint.gap ? GAP_COLORS[dragHint.gap] : "var(--lime)" }}>
                    {dragHint.gap ? `ここに置くと 最短 ${dragHint.gap}` : "ここなら連続出演なし"}
                  </span>
                )}
              </div>
            ) : null)}
        </DragOverlay>
      </DndContext>

      {modal?.type === "copy" && <CopyDialog onClose={() => setModal(null)} onCopy={copyToGenepro} />}
      {modal?.type === "remove-last" && (
        <div className="tt-modal" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="tt-dialog">
            <div className="tt-dlg-h">
              <h3>タイムテーブルから外しますか</h3>
            </div>
            <div className="tt-dlg-b">
              <p>
                「{titleOf(works[modal.workId])}」の本番の出番はこれが最後です。外すと1部・2部・ゲネプロのすべてから消え、左の「タイムテーブルに入っていない演目」に戻ります。
              </p>
              <p>演目そのもの（演目マスター）は消えません。出演が取りやめになった演目は、演目マスター側で取り下げてください。</p>
            </div>
            <div className="tt-dlg-f">
              <button className="tt-btn" onClick={() => setModal(null)}>
                やめる
              </button>
              <button className="tt-btn tt-danger" onClick={() => removeWorkEverywhere(modal.workId)}>
                外す
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && <div className="tt-toast">{toast}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 部品
// ---------------------------------------------------------------------------

/** 「3:20」形式の尺の入力。確定（Enter / フォーカスを外す）したときだけ onCommit。 */
function DurInput({ value, onCommit, className = "tt-dur", title, ariaLabel }) {
  const [text, setText] = useState(fmtDur(value));
  useEffect(() => setText(fmtDur(value)), [value]);
  const commit = () => {
    const v = parseDur(text);
    if (v == null || Number.isNaN(v)) {
      setText(fmtDur(value));
      return;
    }
    if (v !== value) onCommit(v);
  };
  return (
    <input
      className={className}
      value={text}
      title={title}
      aria-label={ariaLabel}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function Part({ id, label, text, className = "" }) {
  const { attributes, listeners, setNodeRef } = useDraggable({ id, data: { label } });
  return (
    <span ref={setNodeRef} className={`tt-part ${className}`} {...attributes} {...listeners}>
      {text || label}
    </span>
  );
}

function FindList({ works, q, kind, appsOf, calc, titleOf, onPick }) {
  const query = q.trim().toLowerCase();
  if (!query && !kind) {
    return <div className="tt-find-list"><div className="tt-find-empty">店舗・イントラ・演目名・生徒名で探すと、その出番へ移動できます。</div></div>;
  }
  const compact = (s) => String(s || "").replace(/\s/g, "");
  const list = works.filter((w) => {
    const apps = appsOf(w.id);
    if (kind === "multi") {
      if (apps.length < 2) return false;
    } else if (kind && w.kind !== kind) return false;
    if (!query) return true;
    const text = `${titleOf(w)} ${w.title} ${subOf(w)} ${w.instructors.join(" ")}`.toLowerCase();
    return text.includes(query) || w.performers.some((p) => compact(p.name + p.kana).includes(compact(query)));
  });
  return (
    <div className="tt-find-list">
      {list.length ? (
        list.slice(0, 60).map((w) => {
          const apps = appsOf(w.id);
          return (
            <button key={w.id} className={`tt-find-item tt-src-${w.kind}`} onClick={() => apps[0] && onPick(apps[0].sid)}>
              <div className="tt-t1">{titleOf(w)}</div>
              <div className="tt-t2">{subOf(w)}</div>
              <div className="tt-apps">
                {apps.length ? apps.map((x) => <span key={x.sid}>{calc[x.k].bySid[x.sid]?.no}</span>) : <span>未配置</span>}
              </div>
            </button>
          );
        })
      ) : (
        <div className="tt-find-empty">見つかりません</div>
      )}
    </div>
  );
}

function StageColumn(props) {
  const { stage, slots, calc, settings, sel, insertAt, onInsertAt, onInsert, usedLabels, onSettings, onCopyGenepro } = props;
  const { setNodeRef } = useDroppable({ id: stage });
  const c = calc[stage];
  const startKey = `${stage}_start_time`;
  let note = null;
  if (stage === "part1" && calc.part2.start != null && c.end != null) {
    const gap = calc.part2.start - c.end;
    note =
      gap >= 0 ? (
        <div className="tt-col-note">
          1部終了 {fmtClock(c.end)} → 2部開始 {fmtClock(calc.part2.start)}　休憩 <b className="tt-mono">{fmtLong(gap)}</b>
        </div>
      ) : (
        <div className="tt-col-note tt-bad">
          1部が2部の開始に <b className="tt-mono">{fmtLong(-gap)}</b> 食い込んでいます
        </div>
      );
  }
  if (stage === "part2" && calc.part1.end != null && c.start != null) {
    const gap = c.start - calc.part1.end;
    note = (
      <div className={`tt-col-note ${gap < 0 ? "tt-bad" : ""}`}>
        1部終了 {fmtClock(calc.part1.end)} から {gap < 0 ? `食い込み ${fmtLong(-gap)}` : `休憩 ${fmtLong(gap)}`}
      </div>
    );
  }
  const lv = [1, 2, 3, 4, 5].map((g) => c.pairs.filter((p) => p.gap === g).length);
  const date = settings[DATES[stage]];
  return (
    <div className="tt-col">
      <div className="tt-col-head">
        <div className="tt-col-title">
          <h3>{STAGE_LABELS[stage]}</h3>
          <span className="tt-date">{date ? date.replaceAll("-", "/") : ""} · Sky シアター MBS</span>
        </div>
        <div className="tt-col-set">
          <label className="tt-field-inline">
            開始
            <input
              type="time"
              className="tt-inp tt-mono"
              defaultValue={settings[startKey]}
              key={settings[startKey]}
              onBlur={(e) => e.target.value !== settings[startKey] && onSettings({ [startKey]: e.target.value })}
            />
          </label>
          {stage === "genepro" && (
            <button className="tt-btn tt-sm" onClick={onCopyGenepro}>
              本番順を複製
            </button>
          )}
        </div>
        <div className="tt-col-stats">
          <span>
            合計 <b className="tt-mono">{fmtLong(c.total)}</b>
          </span>
          <span>
            終了 <b className="tt-mono">{c.end == null ? "開始未設定" : fmtClock(c.end)}</b>
          </span>
          <span>
            作品 <b className="tt-mono">{c.nWork}</b> · 枠 <b className="tt-mono">{c.nFree}</b>
          </span>
        </div>
        {note}
        <div className="tt-col-gaps">
          連続出演{" "}
          {lv.some(Boolean) ? (
            lv.map((n, i) =>
              n ? (
                <span key={i} className="tt-gdot" style={{ background: GAP_COLORS[i + 1] }} title={`${i + 1}ブロック：${n}件`}>
                  {n}
                </span>
              ) : null
            )
          ) : (
            <span className="tt-ok-note">なし</span>
          )}
        </div>
      </div>
      <div className="tt-col-body">
        <button className="tt-ins-top" onClick={() => onInsertAt({ stage, after: null })}>
          ＋ 先頭に枠を挿入
        </button>
        {insertAt && insertAt.stage === stage && insertAt.after === null && (
          <InsertForm labels={usedLabels} defaultSec={settings.default_free_slot_sec} onCancel={() => onInsertAt(null)} onSubmit={(l, d) => onInsert(stage, null, l, d)} />
        )}
        <SortableContext items={slots.map((s) => String(s.id))} strategy={verticalListSortingStrategy}>
          <div className="tt-list" ref={setNodeRef}>
            {slots.map((s) => (
              <SlotRow key={s.id} {...props} slot={s} row={c.bySid[s.id]} selected={sel === s.id} />
            ))}
          </div>
        </SortableContext>
        {stage === "genepro" && !slots.length && (
          <div className="tt-empty-g">
            本番（1部・2部）の並びを複製してから、MC などの枠を足してください。
            <br />
            尺は本番と同じで始まります。リハーサル・場当たりで長く取る作品は、ゲネプロの行で尺を変えられます。
            <br />
            1部と2部の両方に出る作品は、2部の位置に1回だけ入ります。
            <br />
            <button className="tt-btn tt-primary" style={{ marginTop: 10 }} onClick={onCopyGenepro}>
              本番順を複製
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SlotRow({ stage, slot, row, selected, calc, settings, works, flash, insertAt, usedLabels, appsOf, titleOf, onSelect, onInsertAt, onInsert, onUpdateSlot, onRemoveSlot, onWorkDuration }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(slot.id) });
  const style = {
    transform: transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : undefined,
    transition,
  };
  const stop = { onPointerDown: (e) => e.stopPropagation() };
  const showInsert = insertAt && insertAt.stage === stage && insertAt.after === slot.id;
  const flashColor = flash[slot.id];
  const time = <div className="tt-time">{fmtClock(row?.clock)}</div>;

  const co =
    row && row.co != null ? (
      <span className={`tt-co ${slot.changeover_sec_override != null ? "tt-custom" : ""}`} title="クリックして秒数を変更。0:00 で転換なし">
        転換
        <DurInput
          className=""
          value={row.co}
          ariaLabel="この後の転換時間"
          onCommit={(v) => onUpdateSlot(slot.id, { changeover_sec_override: v })}
        />
        <span className="tt-tag">{slot.changeover_sec_override != null ? (row.co === 0 ? "なし" : "個別") : "自動"}</span>
        {slot.changeover_sec_override != null && (
          <button {...stop} onClick={() => onUpdateSlot(slot.id, { changeover_sec_override: null })} title="既定の転換に戻す">
            ↺
          </button>
        )}
      </span>
    ) : null;

  const after = (
    <>
      <div className="tt-after">
        {co}
        <button className="tt-ins" {...stop} onClick={() => onInsertAt({ stage, after: slot.id })}>
          ＋ 枠
        </button>
      </div>
      {showInsert && (
        <InsertForm labels={usedLabels} defaultSec={settings.default_free_slot_sec} onCancel={() => onInsertAt(null)} onSubmit={(l, d) => onInsert(stage, slot.id, l, d)} />
      )}
    </>
  );

  if (slot.slot_type === "free") {
    return (
      <div ref={setNodeRef} style={{ ...style, "--flash": flashColor }} id={`slot-${slot.id}`} className={`tt-slot tt-free ${isDragging ? "tt-dragging" : ""} ${flashColor ? "tt-flash" : ""}`}>
        <div className="tt-row" {...attributes} {...listeners}>
          <div className="tt-no tt-none">—</div>
          {time}
          <LabelInput value={slot.label} labels={usedLabels} onCommit={(v) => onUpdateSlot(slot.id, { label: v })} />
          <div className="tt-side-r">
            <DurInput value={slot.duration_sec || 0} ariaLabel="枠の尺" onCommit={(v) => onUpdateSlot(slot.id, { duration_sec: v })} />
            <button className="tt-del" {...stop} onClick={() => onRemoveSlot(slot.id)} title="この枠を削除">
              ×
            </button>
          </div>
        </div>
        {after}
      </div>
    );
  }

  const w = works[slot.work_id];
  const mg = calc[stage].slotMin[slot.id];
  const apps = w ? appsOf(w.id) : [];
  const ai = apps.findIndex((x) => x.sid === slot.id);
  const dur = slotDurationOf(stage, slot, w, settings);
  return (
    <div
      ref={setNodeRef}
      style={{ ...style, "--flash": flashColor }}
      id={`slot-${slot.id}`}
      className={`tt-slot tt-src-${w?.kind || "tt-class"} ${selected ? "tt-sel" : ""} ${isDragging ? "tt-dragging" : ""} ${flashColor ? "tt-flash" : ""} ${w?.delete_flg ? "tt-withdrawn" : ""}`}
    >
      <div className="tt-row" {...attributes} {...listeners} onClick={() => onSelect(slot.id)}>
        {stage === "genepro" ? (
          <div className="tt-no tt-ref">
            本番<b>{row?.ref || "—"}</b>
          </div>
        ) : (
          <div className="tt-no">{row?.no || "—"}</div>
        )}
        {time}
        <div className="tt-body">
          <div className="tt-t1">
            {w && isLessonWork(w) ? (
              <span className="tt-name">
                {titleOf(w).split("／")[0]}
                <span className="tt-inst">／{w.instructors.join("・")}</span>
              </span>
            ) : (
              titleOf(w)
            )}
            {w && w.kind !== "class" && <span className="tt-chip tt-src">{KIND_LABELS[w.kind]}</span>}
            {w?.is_joint && <span className="tt-chip tt-joint">合同</span>}
            {apps.length > 1 && stage !== "genepro" && (
              <span className="tt-chip tt-multi">
                出番 {ai + 1}/{apps.length}
              </span>
            )}
            {w?.delete_flg && <span className="tt-chip tt-danger">取り下げ済み・外してください</span>}
          </div>
          <div className="tt-t2">{w ? subOf(w) : ""}</div>
        </div>
        <div className="tt-side-col">
          <div className="tt-side-r">
            {mg ? (
              <span className="tt-gapb" style={{ background: GAP_COLORS[mg] }} title={`同じ生徒が ${mg} ブロック先／前に出演`}>
                最短 {mg}
              </span>
            ) : null}
            {w &&
              (stage === "genepro" ? (
                <GeneproDurationField slot={slot} dur={dur} onUpdateSlot={onUpdateSlot} />
              ) : (
                <DurationField work={w} dur={dur} onWorkDuration={onWorkDuration} />
              ))}
          </div>
          <span className="tt-ppl tt-mono">{w && w.kind !== "instructor_show" ? `${w.performers.length}人` : ""}</span>
        </div>
      </div>
      {after}
    </div>
  );
}

function DurationField({ work, dur, onWorkDuration }) {
  if (dur.source === "music") {
    return (
      <span className="tt-lock" title="提出された音源の長さ。変更できません">
        🔒 {fmtDur(dur.sec)}
      </span>
    );
  }
  return (
    <DurInput
      className={`tt-dur ${dur.source === "default" ? "tt-def" : ""}`}
      value={dur.sec}
      title={dur.source === "default" ? "既定の尺（音源の提出前）" : "手入力の尺（音源の提出前）"}
      ariaLabel="尺"
      onCommit={(v) => v > 0 && onWorkDuration(work.id, v)}
    />
  );
}

// ゲネプロの行の尺。何もしなければ本番と同じ尺（音源の長さ）で、変えるとゲネプロの行だけに入る。
// 本番と同じ長さを入れたら、ゲネプロ用の尺は持たない（本番に追従させる）。
function GeneproDurationField({ slot, dur, onUpdateSlot }) {
  return (
    <DurInput
      className={`tt-dur ${dur.source === "genepro" ? "tt-gene" : dur.source === "default" ? "tt-def" : ""}`}
      value={dur.sec}
      title={dur.source === "genepro" ? `ゲネプロ用の尺（本番 ${fmtDur(dur.base.sec)}）` : "本番と同じ尺。変えるとゲネプロだけの尺になります"}
      ariaLabel="ゲネプロの尺"
      onCommit={(v) => v > 0 && onUpdateSlot(slot.id, { duration_sec: v === dur.base.sec ? null : v })}
    />
  );
}

function LabelInput({ value, labels, onCommit }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const listId = "tt-labels";
  return (
    <>
      <input
        className="tt-flabel"
        value={text}
        list={listId}
        aria-label="枠の名前"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          const v = text.trim();
          if (!v) setText(value);
          else if (v !== value) onCommit(v);
        }}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      />
      <datalist id={listId}>
        {labels.map((l) => (
          <option key={l} value={l} />
        ))}
      </datalist>
    </>
  );
}

function InsertForm({ labels, defaultSec, onCancel, onSubmit }) {
  const [label, setLabel] = useState("");
  const [dur, setDur] = useState(fmtDur(defaultSec || 60));
  const [err, setErr] = useState("");
  return (
    <form
      className="tt-insform"
      onSubmit={(e) => {
        e.preventDefault();
        const l = label.trim();
        const d = parseDur(dur);
        if (!l) return setErr("枠の名前を入れてください");
        if (d == null || Number.isNaN(d)) return setErr("尺は 1:30 のように入れてください");
        onSubmit(l, d);
      }}
    >
      <input className="tt-inp tt-lab" autoFocus list="tt-labels-ins" placeholder="枠の名前（例：MC、協賛紹介）" value={label} onChange={(e) => setLabel(e.target.value)} />
      <datalist id="tt-labels-ins">
        {labels.map((l) => (
          <option key={l} value={l} />
        ))}
      </datalist>
      <input className="tt-inp tt-s tt-mono" value={dur} onChange={(e) => setDur(e.target.value)} aria-label="尺" />
      <button className="tt-btn tt-sm tt-primary" type="submit">
        挿入
      </button>
      <button className="tt-btn tt-sm tt-ghost" type="button" onClick={onCancel}>
        取消
      </button>
      {err && <span className="tt-dim" style={{ width: "100%" }}>{err}</span>}
    </form>
  );
}

function CopyDialog({ onClose, onCopy }) {
  const [withFree, setWithFree] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <div className="tt-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tt-dialog">
        <div className="tt-dlg-h">
          <h3>本番順をゲネプロに複製</h3>
        </div>
        <div className="tt-dlg-b">
          <p>1部 → 2部 の作品の並びを、ゲネプロの列に並べます。ゲネプロの尺を変えていた作品は、その尺を引き継ぎます。</p>
          <p>1部と2部の両方に出る作品は、ゲネプロでは2部の位置に1回だけ並べます。ゲネプロの列にある今の並びは消えます（画面を開いている間は「戻す」で戻せます）。</p>
          <label className="tt-toggle">
            <input type="checkbox" checked={withFree} onChange={(e) => setWithFree(e.target.checked)} /> MC などの枠も一緒に複製する
          </label>
        </div>
        <div className="tt-dlg-f">
          <button className="tt-btn" onClick={onClose}>
            やめる
          </button>
          <button
            className="tt-btn tt-primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await onCopy(withFree);
              setBusy(false);
            }}
          >
            複製する
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ sid, stage, slots, works, calc, settings, appsOf, titleOf, onClose, onGoto, onAddAppearance, onRemove, onWorkDuration, onUpdateSlot, onRollcall }) {
  const slot = slots[stage].find((s) => s.id === sid);
  if (!slot || slot.slot_type !== "work") return null;
  const w = works[slot.work_id];
  if (!w) return null;
  const r = calc[stage].bySid[sid];
  const no = stage === "genepro" ? r?.ref : r?.no;
  const anno = calc[stage].anno[sid] || {};
  const apps = appsOf(w.id);
  const gapps = appsOf(w.id, ["genepro"]);
  const isLast = stage !== "genepro" && apps.length === 1;
  const dur = durationOf(w, settings);
  const gdur = slotDurationOf(stage, slot, w, settings);
  const gdiff = gdur.sec - dur.sec;
  const meta = [
    w.lessons[0]?.prefecture,
    KIND_LABELS[w.kind],
    w.kind === "class" && w.lessons.length ? (w.is_joint ? "合同" : "単独") : "",
    w.list_no ? `作品一覧 No.${w.list_no}` : "",
    `演目ID ${w.id}`,
  ].filter(Boolean);
  return (
    <div className="tt-detail">
      <div className="tt-d-head">
        <div className="tt-d-top">
          <span className="tt-d-no">{no || "—"}</span>
          <span className="tt-d-ctx">
            {STAGE_LABELS[stage]}
            {stage === "genepro" ? "（本番の番号）" : ""} · {fmtClock(r?.clock)} 開始 · {r?.pos}ブロック目
          </span>
          <button className="tt-d-close" onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </div>
        <h2>{titleOf(w)}</h2>
        <div className="tt-d-sub">
          {subOf(w)}
          <br />
          {meta.join(" · ")}
        </div>
      </div>
      <div className="tt-d-body">
        <div className="tt-d-sec">
          <h4>
            この演目の出番 <small>番号は出番ごとに付きます</small>
          </h4>
          <div className="tt-apps-d">
            {apps.map((x) => (
              <button key={x.sid} className={`tt-appbtn ${x.sid === sid ? "tt-cur" : ""}`} onClick={() => onGoto(x.sid)}>
                {calc[x.k].bySid[x.sid]?.no}
              </button>
            ))}
            {gapps.map((x) => (
              <button key={x.sid} className={`tt-appbtn ${x.sid === sid ? "tt-cur" : ""}`} onClick={() => onGoto(x.sid)}>
                G{calc.genepro.bySid[x.sid]?.pos}
              </button>
            ))}
          </div>
          <div className="tt-acts">
            <button className="tt-btn tt-sm" onClick={() => onAddAppearance(w.id, "part1")}>
              ＋ 1部に出番を追加
            </button>
            <button className="tt-btn tt-sm" onClick={() => onAddAppearance(w.id, "part2")}>
              ＋ 2部に出番を追加
            </button>
            <button className="tt-btn tt-sm tt-danger" onClick={() => onRemove(sid)}>
              {stage === "genepro" ? "ゲネからこの出番を外す" : isLast ? "タイムテーブルから外す" : "この出番を外す"}
            </button>
          </div>
        </div>
        {stage === "genepro" && (
          <div className="tt-d-sec">
            <h4>
              ゲネプロの尺 <small>このゲネプロの出番だけ</small>
            </h4>
            <div className="tt-side-r">
              <GeneproDurationField slot={slot} dur={gdur} onUpdateSlot={onUpdateSlot} />
              <span className="tt-dim" style={{ fontSize: "var(--t-xs)" }}>
                {gdur.source === "genepro"
                  ? `本番 ${fmtDur(dur.sec)} ／ ${gdiff > 0 ? "+" : gdiff < 0 ? "−" : "±"}${fmtDur(Math.abs(gdiff))}`
                  : "本番と同じ · リハーサル・場当たりで長く取るときは変えてください"}
              </span>
              {gdur.source === "genepro" && (
                <button className="tt-btn tt-sm tt-ghost" onClick={() => onUpdateSlot(slot.id, { duration_sec: null })}>
                  本番と同じに戻す
                </button>
              )}
            </div>
          </div>
        )}
        <div className="tt-d-sec">
          <h4>
            {stage === "genepro" ? "本番の尺" : "尺"} <small>{stage === "genepro" ? "全出番共通" : "全出番共通 · ゲネはゲネの行で変えられます"}</small>
          </h4>
          <div className="tt-side-r">
            <DurationField work={w} dur={dur} onWorkDuration={onWorkDuration} />
            <span className="tt-dim" style={{ fontSize: "var(--t-xs)" }}>
              {dur.source === "music"
                ? "提出された音源の長さ（変更不可）"
                : dur.source === "manual"
                ? "手入力 · 音源が提出されると自動で置き換わります"
                : `既定（${w.kind === "class" && !w.is_joint ? "単独レッスン" : "単独レッスン以外"}）· 音源が提出されると自動で入ります`}
            </span>
            {dur.source === "manual" && (
              <button className="tt-btn tt-sm tt-ghost" onClick={() => onWorkDuration(w.id, null)}>
                既定に戻す
              </button>
            )}
          </div>
        </div>
        <div className="tt-d-sec">
          <h4>
            出演者 {w.kind === "instructor_show" ? "" : `${w.performers.length}人・あいうえお順`}{" "}
            <small>
              「◯つ前／後」は {no || STAGE_LABELS[stage]} が基準。講師は対象外
            </small>
          </h4>
          <ol className="tt-perf">
            {w.kind === "instructor_show" ? (
              <li style={{ gridTemplateColumns: "1fr" }} className="tt-dim">
                イントラショーは全講師が出演します。出演者は表示しません。
              </li>
            ) : w.performers.length ? (
              w.performers.map((p, i) => {
                const a = anno[p.key];
                const mg = a ? Math.min(a.prev ?? 99, a.next ?? 99) : null;
                return (
                  <li key={p.key} style={mg ? { "--pc": GAP_COLORS[mg] } : undefined}>
                    <span className="tt-pno">{i + 1}</span>
                    <span className="tt-pname">
                      <div>{p.name}</div>
                      {p.kana && <small>{p.kana}</small>}
                      {a && (
                        <span className="tt-anno">
                          {a.prev ? <span style={{ background: GAP_COLORS[a.prev] }}>{a.prev}つ前に出番</span> : null}
                          {a.next ? <span style={{ background: GAP_COLORS[a.next] }}>{a.next}つ後に出番</span> : null}
                        </span>
                      )}
                    </span>
                    <span className={`tt-ptype ${p.type}`}>{PERSON_TYPE_LABELS[p.type]}</span>
                  </li>
                );
              })
            ) : (
              <li style={{ gridTemplateColumns: "1fr" }} className="tt-dim">
                出演者がいません。申込やオーディション結果が入ると自動で表示されます。
              </li>
            )}
          </ol>
        </div>
        {stage !== "genepro" && r?.no && w.kind !== "instructor_show" && (
          <button className="tt-btn" onClick={() => onRollcall(sid)}>
            この出番（{r.no}）の点呼表を出力
          </button>
        )}
      </div>
    </div>
  );
}
